import { InboxOutlined } from '@ant-design/icons';
import { Typography } from 'antd';
import { DragEvent, FC, ReactNode, useRef, useState } from 'react';
import useReadonly from 'src/hook/useReadonly';
import { Log } from 'src/service/log.service';
import './XmFileDropZone.less';

interface XmFileDropZoneProps {
  extensions: string[];
  onFile: (file: File) => void;
  title: ReactNode;
  hint?: ReactNode;
  disabled?: boolean;
  ignoreReadonly?: boolean;
  children: ReactNode;
}

const isFileDragInsideZone = (e: DragEvent<HTMLDivElement>): boolean =>
  e.dataTransfer.types.includes('Files') && e.currentTarget.contains(e.target as Node);

const XmFileDropZone: FC<XmFileDropZoneProps> = ({ extensions, onFile, title, hint, disabled: disabledProp, ignoreReadonly, children }) => {
  const { isDisabled: disabled } = useReadonly(ignoreReadonly, disabledProp);
  const [isDragging, setIsDragging] = useState(false);
  const dragTargets = useRef<EventTarget[]>([]);

  if (disabled && isDragging) setIsDragging(false);

  const handleDragEnter = (e: DragEvent<HTMLDivElement>) => {
    if (!isFileDragInsideZone(e)) return;
    dragTargets.current = [...dragTargets.current, e.target];
    if (!disabled) setIsDragging(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    if (!isFileDragInsideZone(e)) return;
    const zone = e.currentTarget;
    // Nodes unmounted mid-drag never fire dragleave, so drop every target no longer in the zone.
    dragTargets.current = dragTargets.current.filter(target => target !== e.target && zone.contains(target as Node));
    if (!dragTargets.current.length) setIsDragging(false);
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    if (!isFileDragInsideZone(e)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = disabled ? 'none' : 'copy';
  };

  // Capture phase keeps a nested antd Upload from handling the drop past these checks.
  const handleDropCapture = (e: DragEvent<HTMLDivElement>) => {
    if (!isFileDragInsideZone(e)) return;
    e.preventDefault();
    e.stopPropagation();
    dragTargets.current = [];
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (disabled || !file) return;
    const name = file.name.toLowerCase();
    if (!extensions.some(ext => name.endsWith(ext))) {
      Log.error(`${file.name} is not a ${extensions.join(' or ')} file`);
      return;
    }
    onFile(file);
  };

  return (
    <div
      className="xm-file-drop-zone"
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDropCapture={handleDropCapture}
    >
      {children}
      {isDragging && (
        <div className="xm-file-drop-zone-overlay">
          <InboxOutlined className="xm-file-drop-zone-icon" />
          <Typography.Title level={5} className="xm-file-drop-zone-title">{title}</Typography.Title>
          {hint && <Typography.Text type="secondary">{hint}</Typography.Text>}
        </div>
      )}
    </div>
  );
};

export default XmFileDropZone;
